"use client";

import { useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  MovingPanel,
  RestPanel,
} from "@/components/tools/physics-fields/mass-energy-panels";
import {
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const MASS_ENERGY_EQUIVALENCE_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeRest: "Energy from mass, or mass from energy (E = mc²)",
  modeMoving: "A moving particle: total and kinetic energy",
  exampleLabel: "Start from an example",
  custom: "Custom values",
  restExamples: {
    gram: "One gram of matter, in GWh",
    kilogram: "One kilogram of matter, in PJ",
    electron: "The electron's rest energy",
    proton: "The proton's rest energy",
    amu: "One atomic mass unit",
    bomb: "The mass converted in a 15 kt explosion",
    sun: "The mass the Sun radiates every second",
    kwh: "The mass equivalent of one kilowatt-hour",
  },
  movingExamples: {
    electron: "An electron with 1 MeV of kinetic energy",
    proton: "A proton at half the speed of light",
    lhc: "A proton with 6.8 TeV of kinetic energy",
    car: "A 1000 kg car at 100 km/h",
  },
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  mass: "Mass m",
  massHint: "Rest mass. The menu includes u, MeV/c² and GeV/c² for particles.",
  massShort: "Mass",
  energy: "Energy E = mc²",
  energyShort: "Energy",
  knownLabel: "What do you know besides the mass?",
  knownSpeed: "The speed",
  knownKinetic: "The kinetic energy",
  speed: "Speed v",
  kinetic: "Kinetic energy K",
  showEnergiesIn: "Show energies in",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  energyJoules: "Energy in joules",
  energyKwh: "Energy in kilowatt-hours",
  energyTnt: "Energy as TNT",
  energyEv: "Energy in electronvolts",
  massKg: "Mass in kilograms",
  totalEnergy: "Total energy E = γmc²",
  restEnergy: "Rest energy E₀ = mc²",
  kineticEnergy: "Kinetic energy K = (γ − 1)mc²",
  classicalKinetic: "Classical ½mv²",
  ratio: "Relativistic ÷ classical K",
  gamma: "Lorentz factor γ",
  beta: "Speed as a fraction of c, β",
  speedResult: "Speed",
  shortfall: "Speed below c, c − v",
  working: "The working",
  thisEnergy: "This energy",
  benchKwh: "1 kWh",
  benchTonne: "1 t TNT",
  benchKilotons: "15 kt TNT",
  benchMegatons: "50 Mt TNT",
  benchSun: "Sun, 1 second",
  scaleLabel: "The energy on a logarithmic scale",
  scaleCaption:
    "A ruler from 10³ J to 10²⁷ J, one tick per thousandfold. The blue marks are familiar yardsticks, with the TNT figures in the convention of 4.184 GJ per tonne.",
  scaleDescription:
    "A horizontal logarithmic scale of energy in joules from one thousand to ten to the twenty-seventh, with labelled marks for one kilowatt-hour, one tonne of TNT, fifteen kilotons, fifty megatons and one second of the Sun's output, and an orange marker at the calculated energy.",
  errorMissing:
    "Fill in every box except the one you are solving for. Scientific notation such as 9.109e-31 works.",
  errorPositive: "The mass and the energy must be greater than zero.",
  errorNegative:
    "Speed and kinetic energy cannot be negative. Enter their size.",
  errorLightSpeed:
    "Nothing with mass can reach or pass the speed of light. Enter a speed below c.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "E = mc² gives the energy equivalent of a mass at rest; it does not mean that all of it can be released. Chemical and nuclear reactions convert only a small fraction of a fuel's mass (about 0.7% for hydrogen fusion), and complete conversion needs matter meeting antimatter. The speed of light is exactly 299 792 458 m/s, and the TNT equivalent uses the convention of 4.184 GJ per tonne.",
} as const;

type Mode = "rest" | "moving";

export default function MassEnergyEquivalenceCalculatorWidget() {
  const strings = useToolUi(MASS_ENERGY_EQUIVALENCE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("rest");
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="me-mode"
        onChange={setMode}
        options={[
          { value: "rest", label: strings.modeRest },
          { value: "moving", label: strings.modeMoving },
        ]}
        value={mode}
      />
      {mode === "rest" ? (
        <RestPanel sig={sig} strings={strings} />
      ) : (
        <MovingPanel sig={sig} strings={strings} />
      )}
      <SigFigsField
        format={fmt}
        id="me-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
